<template>
  <div class="developers">
    <div class="vtm-page-head">
      <div>
        <h1 class="vtm-page-head__title">API &amp; AI</h1>
        <div class="vtm-page-head__sub">Connect AI assistants and your own software to your machines</div>
      </div>
      <a v-if="access" :href="access.referenceUrl" target="_blank" rel="noopener" class="developers__ref">
        <i class="el-icon-reading"></i>API reference
      </a>
    </div>

    <div v-if="loading" v-loading="true" class="developers__loading"></div>

    <div v-else-if="!access || !access.allowed" class="developers__locked">
      <i class="el-icon-lock"></i>
      <p>API &amp; AI isn't turned on for this account yet.</p>
      <p class="developers__locked-sub">Contact support if you'd like access.</p>
    </div>

    <template v-else>
      <div class="sh-seg developers__tabs">
        <el-radio-group v-model="tab" size="small">
          <el-radio-button label="overview">Overview</el-radio-button>
          <el-radio-button label="mcp">AI assistants</el-radio-button>
          <el-radio-button label="keys">API keys</el-radio-button>
          <el-radio-button label="docs">Docs</el-radio-button>
          <el-radio-button label="usage">Usage</el-radio-button>
          <el-radio-button v-if="access.superAdmin" label="customers">All customers</el-radio-button>
        </el-radio-group>
      </div>

      <div class="developers__body">
        <!-- 概览 -->
        <div v-if="tab === 'overview'" class="developers__overview">
          <div class="developers__status">
            <span class="vtm-pill" :class="statusPill.cls">{{ statusPill.text }}</span>
            <span class="vtm-pill vtm-pill--info">Read-only access</span>
            <span class="vtm-pill vtm-pill--ok">Sign-in protected</span>
          </div>

          <div class="developers__cards">
            <button type="button" class="dev-card developers__choice" @click="tab = 'mcp'">
              <span class="developers__choice-icon"><i class="el-icon-chat-dot-round"></i></span>
              <span class="developers__choice-title">Connect an AI assistant</span>
              <span class="developers__choice-sub">Ask Claude or ChatGPT about your machines. No code needed.</span>
              <span class="developers__asks">
                <span v-for="q in sampleAsks" :key="q" class="developers__ask">“{{ q }}”</span>
              </span>
              <span class="developers__choice-cta">Set up in 2 minutes <i class="el-icon-right"></i></span>
            </button>

            <button type="button" class="dev-card developers__choice" @click="tab = 'keys'">
              <span class="developers__choice-icon developers__choice-icon--code"><i class="el-icon-s-operation"></i></span>
              <span class="developers__choice-title">Build with the API</span>
              <span class="developers__choice-sub">Pull machines, stock and sales into your own software, dashboards or spreadsheets.</span>
              <span class="developers__endpoints">
                <code>GET /machines</code>
                <code>GET /inventory</code>
                <code>GET /orders/summary</code>
              </span>
              <span class="developers__choice-cta">Create an API key <i class="el-icon-right"></i></span>
            </button>
          </div>

          <div class="dev-card developers__snapshot">
            <div class="developers__snap">
              <div class="developers__snap-label">Calls this month (all keys)</div>
              <div class="developers__snap-value vtm-num">{{ snapshot.month.toLocaleString() }}</div>
            </div>
            <div class="developers__snap">
              <div class="developers__snap-label">Active keys</div>
              <div class="developers__snap-value vtm-num">{{ snapshot.apiKeys + snapshot.connectors }}</div>
              <div class="developers__snap-sub">{{ snapshot.apiKeys }} API · {{ snapshot.connectors }} AI assistant</div>
            </div>
            <el-button size="small" class="developers__snap-btn" @click="tab = 'usage'">View usage</el-button>
          </div>
        </div>

        <mcp-panel v-else-if="tab === 'mcp'" :mcp-url="access.mcpUrl" @go="tab = $event"></mcp-panel>

        <keys-panel v-else-if="tab === 'keys'" :access="access" @changed="loadSnapshot"></keys-panel>

        <docs-panel v-else-if="tab === 'docs'" :base-url="access.apiBaseUrl" :reference-url="access.referenceUrl" @go="tab = $event"></docs-panel>

        <usage-panel v-else-if="tab === 'usage'"></usage-panel>

        <template v-else-if="tab === 'customers'">
          <div v-if="drill" class="developers__crumb">
            <a href="#" @click.prevent="drill = null"><i class="el-icon-arrow-left"></i>All customers</a>
            <span>/</span>
            <b>{{ drill.email || drill.userId }}</b>
          </div>
          <usage-panel
            v-if="drill"
            :key="drill.userId"
            :user-id="drill.userId"
            :title="drill.email || 'Customer usage'"
            subtitle="This customer's calls, by key and by machine"
          ></usage-panel>
          <usage-panel v-else admin title="All customers" subtitle="API and AI assistant calls across every account" @drill="drill = $event"></usage-panel>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
  // API & AI（前端静态路由 /developers）：AI 助手连接（MCP）、自助 API 密钥、文档、用量；超级管理员可看全部客户
  import McpPanel from "@/components/developers/McpPanel";
  import KeysPanel from "@/components/developers/KeysPanel";
  import DocsPanel from "@/components/developers/DocsPanel";
  import UsagePanel from "@/components/developers/UsagePanel";
  import { MCP_TOOLS, isConnectorKey, toCount } from "@/components/developers/developerContent";

  const TABS = ["overview", "mcp", "keys", "docs", "usage", "customers"];

  export default {
    name: "Developers",
    components: { McpPanel, KeysPanel, DocsPanel, UsagePanel },
    data() {
      const fromQuery = this.$route && this.$route.query.tab;
      return {
        loading: true,
        access: null,
        tab: TABS.indexOf(fromQuery) !== -1 ? fromQuery : "overview",
        drill: null,
        snapshot: { month: 0, apiKeys: 0, connectors: 0 },
        sampleAsks: MCP_TOOLS.slice(0, 3).map(t => t.ask)
      };
    },
    computed: {
      statusPill() {
        if (this.access.staff) {
          return { cls: "vtm-pill--info", text: "Staff access · whole fleet" };
        }
        return this.access.subscribed
          ? { cls: "vtm-pill--ok", text: "API access active" }
          : { cls: "vtm-pill--warn", text: "Needs an active subscription" };
      }
    },
    watch: {
      // 标签写进地址栏，刷新/分享链接时停留在同一标签
      tab(value) {
        if (this.$route.query.tab !== value) {
          this.$router.replace({ query: { ...this.$route.query, tab: value } }).catch(() => {});
        }
      }
    },
    created() {
      this.$api
        .request(this.$url.ApiKey.access, this.$method.get)
        .then(res => {
          if (res && res.code === 0) {
            this.access = res.data;
            if (this.access.allowed) {
              this.loadSnapshot();
            }
          }
        })
        .finally(() => (this.loading = false));
    },
    methods: {
      loadSnapshot() {
        this.$api.request(this.$url.ApiKey.list, this.$method.get).then(res => {
          if (res && res.code === 0) {
            const keys = res.data || [];
            const active = keys.filter(k => k.isActive);
            this.snapshot = {
              // 已撤销的密钥本月也可能有调用，一并计入
              month: keys.reduce((sum, k) => sum + toCount(k.callsThisMonth), 0),
              apiKeys: active.filter(k => !isConnectorKey(k)).length,
              connectors: active.filter(k => isConnectorKey(k)).length
            };
          }
        });
      }
    }
  };
</script>

<style scoped lang="scss">
  .developers__ref {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink-2);
    text-decoration: none;
    padding: 6px 10px;
    border-radius: var(--vtm-r-sm);

    &:hover {
      color: var(--vtm-ink);
      background: var(--vtm-well);
    }
  }

  .developers__loading {
    height: 240px;
  }

  .developers__locked {
    text-align: center;
    padding: 80px 20px;
    color: var(--vtm-ink-2);

    i {
      font-size: 40px;
      color: var(--vtm-ink-3);
    }

    p {
      margin: 10px 0 0;
      font-size: 14px;
    }
  }

  .developers__locked-sub {
    font-size: 12.5px !important;
    color: var(--vtm-ink-3);
  }

  .developers__tabs {
    padding: 10px 16px 8px;
  }

  .developers__body {
    padding: 8px 16px 32px;
    max-width: 1180px;
  }

  .developers__status {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
  }

  .developers__cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;

    @media (max-width: 900px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .developers__choice {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    font: inherit;
    cursor: pointer;
    transition: border-color var(--vtm-speed), box-shadow var(--vtm-speed), transform var(--vtm-speed);

    &:hover {
      border-color: var(--vtm-mint-dim);
      box-shadow: var(--vtm-e2);
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--vtm-focus-ring);
    }
  }

  .developers__choice-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--vtm-mint-soft);
    color: var(--vtm-mint-ink);
    font-size: 20px;

    &--code {
      background: var(--vtm-info-soft);
      color: var(--vtm-info);
    }
  }

  .developers__choice-title {
    margin-top: 14px;
    font-family: var(--vtm-display);
    font-size: 16px;
    font-weight: 650;
    color: var(--vtm-heading);
  }

  .developers__choice-sub {
    margin-top: 4px;
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--vtm-ink-2);
  }

  .developers__asks,
  .developers__endpoints {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 14px;
  }

  .developers__ask {
    font-size: 12.5px;
    color: var(--vtm-ink);
    background: var(--vtm-well);
    border-radius: 14px;
    padding: 4px 10px;
  }

  .developers__endpoints code {
    font-family: var(--vtm-mono);
    font-size: 12px;
    color: var(--vtm-ink);
    background: var(--vtm-well);
    border-radius: 4px;
    padding: 3px 7px;
  }

  .developers__choice-cta {
    margin-top: auto;
    padding-top: 16px;
    font-size: 13px;
    font-weight: 650;
    color: var(--vtm-mint-ink);
  }

  .developers__snapshot {
    margin-top: 14px;
    display: flex;
    align-items: center;
    gap: 32px;
    flex-wrap: wrap;
  }

  .developers__snap-label {
    font-size: 12.5px;
    color: var(--vtm-ink-2);
  }

  .developers__snap-sub {
    font-size: 12px;
    color: var(--vtm-ink-2);
  }

  .developers__snap-value {
    font-family: var(--vtm-display);
    font-size: 22px;
    font-weight: 650;
    color: var(--vtm-heading);
  }

  .developers__snap-btn {
    margin-left: auto;
  }

  .developers__crumb {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    font-size: 13px;
    color: var(--vtm-ink-3);

    a {
      color: var(--vtm-mint-ink);
      font-weight: 600;
      text-decoration: none;
    }

    b {
      color: var(--vtm-ink);
    }
  }
</style>

<style lang="scss">
  // 子面板共用：卡片、分区标题、行、折叠项、提示条（.dev- 前缀，仅本页使用）
  .developers {
    .dev-section {
      margin-bottom: 22px;
    }

    .dev-section__title {
      margin: 0;
      font-family: var(--vtm-display);
      font-size: 16px;
      font-weight: 650;
      color: var(--vtm-heading);
    }

    .dev-section__sub {
      margin: 2px 0 12px;
      font-size: 13px;
      color: var(--vtm-ink-2);
    }

    .dev-section__title + .dev-card {
      margin-top: 12px;
    }

    .dev-card {
      background: var(--vtm-surface);
      border: 1px solid var(--vtm-line);
      border-radius: var(--vtm-r-lg);
      box-shadow: var(--vtm-e1);
      padding: 18px 20px;

      &--flush {
        padding: 0;
        overflow: hidden;
      }
    }

    .dev-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 14px 0;
      border-bottom: 1px solid var(--vtm-line);

      &:first-child {
        padding-top: 0;
      }

      &:last-child {
        border-bottom: 0;
        padding-bottom: 0;
      }

      &--stack {
        display: block;
      }
    }

    .dev-row__text {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .dev-row__title {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      font-size: 14.5px;
      font-weight: 650;
      color: var(--vtm-heading);
    }

    .dev-row__sub {
      margin-top: 3px;
      font-size: 13.5px;
      line-height: 1.5;
      color: var(--vtm-ink-2);
    }

    .dev-acc {
      border-top: 1px solid var(--vtm-line);

      .dev-card > &:first-child {
        border-top: 0;
      }
    }

    .dev-row + .dev-acc {
      margin-top: 14px;
    }

    .dev-acc__head {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 14px 0;
      background: none;
      border: 0;
      text-align: left;
      font: inherit;
      cursor: pointer;

      &:focus-visible {
        outline: none;
        box-shadow: var(--vtm-focus-ring);
        border-radius: var(--vtm-r-sm);
      }
    }

    .dev-card > .dev-acc:first-child .dev-acc__head {
      padding-top: 0;
    }

    .dev-card > .dev-acc:last-child:not(.is-open) .dev-acc__head {
      padding-bottom: 0;
    }

    .dev-acc__chev {
      flex-shrink: 0;
      font-size: 16px;
      color: var(--vtm-ink-2);
      transition: transform var(--vtm-speed);
    }

    .dev-acc.is-open .dev-acc__chev {
      transform: rotate(180deg);
    }

    .dev-acc__body {
      padding: 0 0 16px;
    }

    .dev-banner {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 12px 14px;
      border-radius: var(--vtm-r-md);
      font-size: 13px;
      line-height: 1.5;
      margin-bottom: 16px;

      i {
        font-size: 16px;
        margin-top: 1px;
      }

      &--warn {
        background: var(--vtm-warn-soft);
        color: var(--vtm-warn);
      }
    }
  }
</style>
